import { HeatMapCoordinate } from './../../models/heatmap.model';
import { StatusCount } from '../../models/status-count.model';
import { ExternalScanCount } from '../../models/external-scan-count.model';
import { Vendor } from './../../models/vendor.model';
import { Injectable } from '@angular/core';
import { HeatMap } from 'models/heatmap.model';
import { ImpactEnum, ImpactTextEnum } from '../shared/enums/impact.enum';
import { StatusEnum } from '../shared/enums/status.enum';
import { ImpactCount } from 'models/impact-count.model';
import { HeatmapSectionEnum } from 'app/shared/enums/heatmap-sections.enum';
import { TableHeader } from 'models/table-header.model';
import { SortDirectionEnum } from 'app/shared/enums/sort-direction.enum';
import { UtilsService } from 'app/shared/utils.service';

const SORT_NAME = 'name';
const SORT_SCORE = 'score';
const SORT_TARGET = 'target';

const CYGOV_HEATMAP_SECTIONS = 'cygov_heatmap_sections_storage';

@Injectable({
  providedIn: 'root',
})
export class ThirdPartyService {
  isVendorListExpanded: boolean = false;
  storage: Storage;

  static getTotalVendors(vendors: Vendor[]) {
    return vendors.length;
  }

  static getRiskScoreAverage(vendors: Vendor[]) {
    return (
      vendors.reduce((average, vendor) => (average += vendor.scores.total), 0) /
      this.getTotalVendors(vendors)
    );
  }

  static getTarget(vendors: Vendor[]) {
    return 9.5;
  }

  static getImpactCounts(vendors: Vendor[]): ImpactCount {
    const impactCount = new ImpactCount();
    vendors.forEach(vendor => {
      const impact = vendor.vendorDetails.impact;

      if (impact >= 0 && impact <= ImpactEnum.LOW) {
        impactCount.low++;
      } else if (impact <= 50) {
        impactCount.medium++;
      } else if (impact <= 80) {
        impactCount.high++;
      } else {
        impactCount.veryHigh++;
      }
    });

    return impactCount;
  }

  static getExternalScanCounts(vendors: Vendor[], externalScanCount: ExternalScanCount) {
    vendors.forEach(vendor => {
      const externalScan = vendor.vendorDetails.externalScan;

      if (externalScan > 0) {
        externalScanCount.failed++;
      } else {
        externalScanCount.cleared++;
      }
    });
  }

  static getStatusCounts(vendors: Vendor[], statusCount: StatusCount) {
    vendors.forEach(vendor => {
      switch (true) {
        case vendor.vendorDetails.status === StatusEnum.IN_PROCESS:
          statusCount.inProcess++;
          break;

        case vendor.vendorDetails.status === StatusEnum.PENDING:
          statusCount.pending++;
          break;

        case vendor.vendorDetails.status === StatusEnum.APPROVED:
          statusCount.approved++;
          break;

        case vendor.vendorDetails.status === StatusEnum.DENIED:
          statusCount.denied++;
          break;
      }
    });
  }

  static getHeatMapData(vendors: Vendor[], heatMap: HeatMap[]) {
    vendors.forEach(vendor => {
      const coordinate = new HeatMap();
      coordinate.probability = vendor.vendorDetails.probability;
      coordinate.impactValue = vendor.vendorDetails.impact;
      coordinate.impact = ThirdPartyService.getImpact(
        coordinate.impactValue,
        coordinate.probability
      );
      coordinate.vendorName = vendor.name;
      coordinate.vendorId = vendor.id;
      heatMap.push(coordinate);
    });
  }

  static getHeatMapCoordinates(
    heatMapData: HeatMap[],
    heatMapCoordinate: HeatMapCoordinate[],
    minX: number,
    minY: number,
    chartWidth: number,
    chartHeight: number
  ) {
    heatMapData.forEach(heatMap => {
      if (heatMap.probability < 0.7) {
        heatMap.probability = 0.7;
      } else if (heatMap.probability > 99.3) {
        heatMap.probability = 99.3;
      }
      if (heatMap.impactValue < 1.3) {
        heatMap.impactValue = 1.3;
      } else if (heatMap.impactValue >= 98.7) {
        heatMap.impactValue = 98.7;
      }
      const coordinateX = parseFloat((minX + (chartWidth / 100) * heatMap.probability).toFixed(2));
      const coordinateY = parseFloat(
        (minY + chartHeight - (chartHeight / 100) * heatMap.impactValue).toFixed(2)
      );
      console.log(heatMap.impact);
      const color =
        heatMap.impact === ImpactTextEnum.VERY_HIGH
          ? 'url(#Gradient-DarkRed)'
          : heatMap.impact === ImpactTextEnum.HIGH
          ? 'url(#Gradient-Red)'
          : heatMap.impact === ImpactTextEnum.MEDIUM
          ? 'url(#Gradient-Yellow)'
          : 'url(#Gradient-Green)';
      heatMapCoordinate.push(
        new HeatMapCoordinate(coordinateX, coordinateY, color, heatMap.vendorId, heatMap.vendorName)
      );
    });
  }

  static sortVendorList(vendors: Vendor[], filter: string): Vendor[] {
    return vendors.sort((v1: Vendor, v2: Vendor) => {
      switch (filter) {
        case SORT_NAME:
          const v1Name = v1.name.toLowerCase();
          const v2Name = v2.name.toLowerCase();
          if (v1Name < v2Name) {
            return -1;
          }
          if (v2Name > v1Name) {
            return 1;
          }
          return 0;
        case SORT_SCORE:
          return v2.scores.total - v1.scores.total;
        case SORT_TARGET:
          break;
      }
    });
  }

  static filterVendorList(vendors: Vendor[], filter: string): Vendor[] {
    return vendors.filter(v => {
      switch (filter) {
        case ImpactTextEnum.LOW:
          return (
            this.getImpact(v.vendorDetails.impact, v.vendorDetails.probability) ===
            ImpactTextEnum.LOW
          );
        case ImpactTextEnum.MEDIUM:
          return (
            this.getImpact(v.vendorDetails.impact, v.vendorDetails.probability) ===
            ImpactTextEnum.MEDIUM
          );
        case ImpactTextEnum.HIGH:
          return (
            this.getImpact(v.vendorDetails.impact, v.vendorDetails.probability) ===
            ImpactTextEnum.HIGH
          );
        case ImpactTextEnum.VERY_HIGH:
          return (
            this.getImpact(v.vendorDetails.impact, v.vendorDetails.probability) ===
            ImpactTextEnum.VERY_HIGH
          );
      }
    });
  }

  static filterVendorListByText(vendors: Vendor[], searchText: string): Vendor[] {
    return vendors.filter(v => v.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
  }

  static getImpact(impact: number, probability: number) {
    if (
      (impact >= 0 &&
        impact <= ImpactEnum.LOW &&
        probability >= 0 &&
        probability <= ImpactEnum.LOW) ||
      (impact >= 0 &&
        impact <= ImpactEnum.LOW &&
        probability > ImpactEnum.LOW &&
        probability <= ImpactEnum.MEDIUM) ||
      (impact > ImpactEnum.LOW &&
        impact <= ImpactEnum.MEDIUM &&
        probability >= 0 &&
        probability <= ImpactEnum.LOW)
    ) {
      return ImpactTextEnum.LOW;
    }
    if (
      (impact >= 0 &&
        impact <= ImpactEnum.LOW &&
        probability > ImpactEnum.MEDIUM &&
        probability <= ImpactEnum.HIGH) ||
      (impact > ImpactEnum.LOW &&
        impact <= ImpactEnum.MEDIUM &&
        probability > ImpactEnum.LOW &&
        probability <= ImpactEnum.MEDIUM) ||
      (impact > ImpactEnum.MEDIUM &&
        impact <= ImpactEnum.HIGH &&
        probability >= 0 &&
        probability <= ImpactEnum.LOW)
    ) {
      return ImpactTextEnum.MEDIUM;
    }
    if (
      (impact >= 0 &&
        impact <= ImpactEnum.LOW &&
        probability > ImpactEnum.HIGH &&
        probability <= ImpactEnum.VERY_HIGH) ||
      (impact > ImpactEnum.LOW &&
        impact <= ImpactEnum.MEDIUM &&
        probability > ImpactEnum.MEDIUM &&
        probability <= ImpactEnum.HIGH) ||
      (impact > ImpactEnum.LOW &&
        impact <= ImpactEnum.MEDIUM &&
        probability > ImpactEnum.HIGH &&
        probability <= ImpactEnum.VERY_HIGH) ||
      (impact > ImpactEnum.MEDIUM &&
        impact <= ImpactEnum.HIGH &&
        probability > ImpactEnum.LOW &&
        probability <= ImpactEnum.MEDIUM) ||
      (impact > ImpactEnum.MEDIUM &&
        impact <= ImpactEnum.HIGH &&
        probability > ImpactEnum.MEDIUM &&
        probability <= ImpactEnum.HIGH) ||
      (impact > ImpactEnum.HIGH &&
        impact <= ImpactEnum.VERY_HIGH &&
        probability >= 0 &&
        probability <= ImpactEnum.LOW) ||
      (impact > ImpactEnum.HIGH &&
        impact <= ImpactEnum.VERY_HIGH &&
        probability > ImpactEnum.LOW &&
        probability <= ImpactEnum.MEDIUM)
    ) {
      return ImpactTextEnum.HIGH;
    }
    if (
      (impact > ImpactEnum.MEDIUM &&
        impact <= ImpactEnum.HIGH &&
        probability > ImpactEnum.HIGH &&
        probability <= ImpactEnum.VERY_HIGH) ||
      (impact > ImpactEnum.HIGH &&
        impact <= ImpactEnum.VERY_HIGH &&
        probability > ImpactEnum.MEDIUM &&
        probability <= ImpactEnum.HIGH) ||
      (impact > ImpactEnum.HIGH &&
        impact <= ImpactEnum.VERY_HIGH &&
        probability > ImpactEnum.HIGH &&
        probability <= ImpactEnum.VERY_HIGH)
    ) {
      return ImpactTextEnum.VERY_HIGH;
    }
    return ImpactTextEnum.VERY_HIGH;
  }

  static getImpactText(impact: number) {
    if (impact >= 0 && impact <= ImpactEnum.LOW) {
      return ImpactTextEnum.LOW;
    } else if (impact <= ImpactEnum.MEDIUM) {
      return ImpactTextEnum.MEDIUM;
    } else if (impact <= ImpactEnum.HIGH) {
      return ImpactTextEnum.HIGH;
    } else {
      return ImpactTextEnum.VERY_HIGH.split(' ').join('-');
    }
  }

  static sortArray(sortData: TableHeader, data: Vendor[]) {
    const ascendingOrder = sortData.sortDirection === SortDirectionEnum.ASCENDING ? 1 : -1;
    const keys: string[] = sortData.value.split('.');

    switch (keys.length) {
      case 1:
        data.sort((a, b) => {
          return a[keys[0]] < b[keys[0]]
            ? ascendingOrder * -1
            : b[keys[0]] < a[keys[0]]
            ? ascendingOrder
            : 0;
        });
        break;
      case 2:
        data.sort((a, b) => {
          return a[keys[0]][keys[1]] < b[keys[0]][keys[1]]
            ? ascendingOrder * -1
            : b[keys[0]][keys[1]] < a[keys[0]][keys[1]]
            ? ascendingOrder
            : 0;
        });
        break;
      case 2:
        data.sort((a, b) => {
          return a[keys[0]][keys[1]][keys[2]] < b[keys[0]][keys[1]][keys[2]]
            ? ascendingOrder * -1
            : b[keys[0]][keys[1]][keys[2]] < a[keys[0]][keys[1]][keys[2]]
            ? ascendingOrder
            : 0;
        });
        break;
      default:
    }
  }

  static vendorGroupList(vendorList: Vendor[] = []): string[] {
    const groupMap = { general: true };
    vendorList.forEach(vendor => {
      if (UtilsService.isDefined(vendor.vendorDetails.group)) {
        groupMap[vendor.vendorDetails.group] = true;
      }
    });
    return Object.keys(groupMap);
  }

  constructor() {
    this.storage = localStorage;
  }

  toggleVendorList(isExpanded: boolean) {
    this.isVendorListExpanded = isExpanded;
  }

  getHeatmapDefaultView(): number {
    let sections: number | string;
    return (sections = this.storage.getItem(CYGOV_HEATMAP_SECTIONS))
      ? +sections
      : HeatmapSectionEnum.None;
  }

  setHeatmapDefaultView(sections: HeatmapSectionEnum): void {
    this.storage.setItem(CYGOV_HEATMAP_SECTIONS, sections.toString());
  }
}
